"use client";

import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useConfirmation } from "@/components/common/ConfirmationDialog";
import { TableContainer } from "@/components/common/TableContainer";
import { DataView } from "@/components/common/DataView";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Plus,
  Edit,
  Trash2,
  Calendar,
  Users,
  Send,
  BarChart3,
  RefreshCw,
} from "lucide-react";
import { ColumnDef } from "@tanstack/react-table";
import { ActionButton } from "@/components/common/DataViewTypes";
import { useNavigate, useParams } from "react-router-dom";
import { RootState, AppDispatch } from "@/store/store";
import {
  fetchMarketingCampaignsRequest,
  deleteMarketingCampaignRequest,
  sendMarketingCampaignRequest,
} from "@/store/slices/marketingCampaign.slice";
import { MarketingCampaign } from "@/types/models/marketing";

const CampaignPage = () => {
  const navigate = useNavigate();
  const { storeType, storeId } = useParams<{ storeType: string; storeId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const { confirm } = useConfirmation();
  const [pageIndex, setPageIndex] = useState(0);

  const {
    items: campaigns,
    loaded,
    loading,
    error,
    pagination,
  } = useSelector((state: RootState) => state.marketingCampaigns);

  const fetchCampaigns = useCallback(() => {
    dispatch(fetchMarketingCampaignsRequest({ page: pageIndex + 1 }));
  }, [dispatch, pageIndex]);

  useEffect(() => {
    if (loading) return;

    const hasNotFoundError = error?.status === 404;
    if (hasNotFoundError) return;

    const currentPageIndex = Math.max(0, (pagination?.page || 1) - 1);
    const hasPageMismatch = currentPageIndex !== pageIndex;

    if (!loaded && (campaigns?.length || 0) === 0) {
      fetchCampaigns();
      return;
    }

    if (loaded && hasPageMismatch) {
      fetchCampaigns();
    }
  }, [loaded, campaigns?.length, loading, error?.status, pagination?.page, pageIndex, fetchCampaigns]);

  const getStatusBadge = (status: MarketingCampaign["status"]) => {
    const statusConfig = {
      draft: {
        color: "bg-gray-500/20 text-gray-600 hover:bg-gray-500/20",
        label: "Draft",
      },
      active: {
        color: "bg-blue-500/20 text-blue-600 hover:bg-blue-500/20",
        label: "Active",
      },
      paused: {
        color: "bg-yellow-500/20 text-yellow-600 hover:bg-yellow-500/20",
        label: "Paused",
      },
      completed: {
        color: "bg-green-500/20 text-green-600 hover:bg-green-500/20",
        label: "Completed",
      },
    };

    return (
      <Badge className={statusConfig[status].color}>
        {statusConfig[status].label}
      </Badge>
    );
  };

  const getChannelBadge = (channel: "sms" | "whatsapp" | "email") => {
    const channelColors = {
      email: "bg-blue-500/20 text-blue-600 hover:bg-blue-500/20",
      sms: "bg-green-500/20 text-green-600 hover:bg-green-500/20",
      whatsapp: "bg-emerald-500/20 text-emerald-600 hover:bg-emerald-500/20",
    };

    const channelLabels = {
      email: "Email",
      sms: "SMS",
      whatsapp: "WhatsApp",
    };

    return (
      <Badge className={channelColors[channel]}>{channelLabels[channel]}</Badge>
    );
  };

  const handleDelete = async (campaignId: string) => {
    const result = await confirm({
      title: "Delete Campaign",
      message:
        "Are you sure you want to delete this campaign? This action cannot be undone.",
      confirmText: "Delete",
      cancelText: "Cancel",
      type: "destructive",
    });

    if (result.confirmed) {
      dispatch(deleteMarketingCampaignRequest({ id: campaignId }));
    }
  };

  const handleSend = async (campaignId: string) => {
    const result = await confirm({
      title: "Send Campaign",
      message:
        "Are you sure you want to send this campaign? This action cannot be undone.",
      confirmText: "Send",
      cancelText: "Cancel",
      type: "default",
    });

    if (result.confirmed) {
      dispatch(sendMarketingCampaignRequest({ id: campaignId }));
    }
  };

  const handleEdit = (campaignId: string) => {
    navigate(
      `/${storeType}/${storeId}/marketing/campaign/${campaignId}/edit`,
    );
  };

  const handleRowClick = (campaign: MarketingCampaign) => {
    handleEdit(campaign.id);
  };

  const handleCreateNew = () => {
    navigate(`/${storeType}/${storeId}/marketing/campaign/create`);
  };

  const handleRefresh = () => {
    fetchCampaigns();
  };

  const columns: ColumnDef<MarketingCampaign>[] = [
    {
      accessorKey: "title",
      header: "Campaign",
      minSize: 300,
      size: 350,
      cell: ({ row }) => {
        const campaign = row.original;
        return (
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center">
              <Send className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="font-medium text-foreground">{campaign.title}</p>
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <Users className="h-3 w-3" />
                {campaign.recipient_type} •{" "}
                {campaign.sent_count?.toLocaleString() || 0} sent
              </p>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "channel",
      header: "Channel",
      cell: ({ row }) => {
        return getChannelBadge(row.original.channel);
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => {
        const campaign = row.original;
        return (
          <div className="space-y-1">
            {getStatusBadge(campaign.status)}
            {campaign.scheduled_at && (
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {new Date(campaign.scheduled_at).toLocaleDateString()}
              </p>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "performance",
      header: "Performance",
      cell: ({ row }) => {
        const campaign = row.original;

        if (campaign.status !== "completed" || !campaign.open_rate) {
          return (
            <div className="text-sm text-muted-foreground">
              {campaign.status === "draft" && "Not sent yet"}
              {campaign.status === "active" && "In progress"}
              {campaign.status === "paused" && "Paused"}
            </div>
          );
        }

        return (
          <div className="text-sm">
            <div className="flex items-center gap-2 font-medium">
              <BarChart3 className="h-3 w-3" />
              {campaign.open_rate}% opened
            </div>
            <p className="text-muted-foreground">
              {campaign.click_rate}% clicked
            </p>
          </div>
        );
      },
    },
    {
      accessorKey: "created_at",
      header: "Created",
      cell: ({ row }) => {
        return (
          <div className="text-sm text-muted-foreground">
            {new Date(row.original.created_at).toLocaleDateString()}
          </div>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const campaign = row.original;
        return (
          <div className="flex items-center gap-1">
            {campaign.status === "draft" && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 text-green-600 hover:text-green-700"
                onClick={() => handleSend(campaign.id)}
              >
                <Send className="h-4 w-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => handleEdit(campaign.id)}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
              onClick={() => handleDelete(campaign.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        );
      },
    },
  ];

  // Create action buttons
  const actionButtons: ActionButton[] = [
    {
      label: "Refresh",
      icon: <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />,
      onClick: handleRefresh,
      variant: "outline",
      disabled: loading,
    },
    {
      label: "New Campaign",
      icon: <Plus className="h-3 w-3" />,
      onClick: handleCreateNew,
      variant: "primary",
    },
  ];

  return (
    <div>
      <TableContainer>
        <div className="bg-secondary-background rounded-lg p-4">
          <DataView
            title="Marketing Campaigns"
            description="Create and manage targeted marketing campaigns to engage your customers"
            columns={columns}
            data={campaigns}
            pagination={pagination}
            pageIndex={pageIndex}
            onPageChange={setPageIndex}
            loading={loading}
            emptyMessage="No campaigns found. Create your first campaign to get started."
            actionButtons={actionButtons}
            enableColumnResizing={true}
            onRowClick={handleRowClick}
          />
        </div>
      </TableContainer>
    </div>
  );
};

export default CampaignPage;
